// マスタCSVの2つのポップアップが表示するために必要な判断と整形をまとめたモジュール。
// 選択状態の計算、テーブル名から画面名への言い換え、不備一覧の絞り込みなど、
// 描画に先立って決まる内容をすべてここに置く。React 側には並べる仕事だけを残す。

import type {
  MasterImportApplied,
  MasterImportApproval,
  MasterImportCollision,
  MasterImportError,
  MasterImportIdPolicy,
  MasterImportMode,
  MasterImportRename,
  MasterImportRequest,
  MasterImportResponse,
} from "../../../../schema/console/master_import";
import { ApiError, ErrorCode } from "../../../../api/ApiError";
import { limitedListOf } from "../../../../util/limitedList";
import { errorMessageOf } from "../../../../util/typed";
import type { MasterCsvFileError, MasterCsvTable } from "./masterCsvFiles";
import type { MasterScreenKey, MasterSystemTier } from "./masterCsvTargets";
import { MASTER_CSV_TARGETS, labelOfScreenKey, screenLabelOfCollectionKey } from "./masterCsvTargets";

// 一覧に一度に出す不備の上限。これを超えた分は件数だけを伝える。
export const MAX_DISPLAYED_ERROR_COUNT = 100;

// 選択できるマスタの総数。全選択チェックボックスの見え方を決めるときに使う。
export const SELECTABLE_SCREEN_COUNT = MASTER_CSV_TARGETS.length;

// -----------------------------------------------------------------------------
// エクスポートの選択状態
// -----------------------------------------------------------------------------

// 全選択チェックボックスの見え方。partial は一部だけ選ばれている中間表示を表す。
export type SelectAllState = "none" | "partial" | "all";

export function selectAllStateOf(selectedCount: number, totalCount: number): SelectAllState {
  const isAllSelected = totalCount > 0 && selectedCount >= totalCount;
  if (isAllSelected) {
    return "all";
  }
  if (selectedCount > 0) {
    return "partial";
  }
  return "none";
}

// 1つのマスタのチェックを押したあとの選択内容。既に選ばれていれば外し、選ばれていなければ加える。
export function toggledScreenKeys(selectedKeys: readonly MasterScreenKey[], screenKey: MasterScreenKey): MasterScreenKey[] {
  if (selectedKeys.includes(screenKey)) {
    return selectedKeys.filter((key) => key !== screenKey);
  }
  return [...selectedKeys, screenKey];
}

// 全選択チェックボックスを押したあとの選択内容。
// すべて選ばれているときだけ全解除にし、中間表示のときは残りを選び足す。
export function toggledAllScreenKeys(selectedKeys: readonly MasterScreenKey[]): MasterScreenKey[] {
  const isAllSelected = selectAllStateOf(selectedKeys.length, SELECTABLE_SCREEN_COUNT) === "all";
  if (isAllSelected) {
    return [];
  }
  return MASTER_CSV_TARGETS.map((target) => target.screenKey);
}

// 系統ごとにまとめたマスタの並び。ポップアップの見出しと行の順番はこれに従う。
export type MasterCsvScreenGroup = { system: MasterSystemTier; label: string; screenKeys: MasterScreenKey[] };

const SYSTEM_LABEL: Record<MasterSystemTier, string> = {
  division: "マスタ区分",
  work: "工事マスタ",
  material: "材料マスタ",
};

const SYSTEM_ORDER: readonly MasterSystemTier[] = ["division", "work", "material"];

export const MASTER_CSV_SCREEN_GROUPS: readonly MasterCsvScreenGroup[] = SYSTEM_ORDER.map((system) => {
  const screenKeys = MASTER_CSV_TARGETS.filter((target) => target.system === system).map((target) => target.screenKey);
  return { system, label: SYSTEM_LABEL[system], screenKeys };
});

// -----------------------------------------------------------------------------
// テーブル名の言い換え
// -----------------------------------------------------------------------------

// サーバーはテーブル名で結果を返すため、利用者に見せる前にマスタ編集画面の名前へ言い換える。
// 1つの画面が複数テーブルを束ねている場合は画面名だけを出す。
// 対応表そのものは masterCsvTargets に置くため、ここでは同じ名前で再エクスポートし、
// 既にこれらの関数を import しているモジュールから見える参照経路を変えないようにする。
export { labelOfScreenKey, screenLabelOfCollectionKey };

// -----------------------------------------------------------------------------
// 不備一覧
// -----------------------------------------------------------------------------

// 不備一覧の1行分。前処理の不備もサーバーからの不備も同じ形にして同じ表で見せる。
// 行番号と列名は特定できないことがあるため、表に出せる文字列として持つ。
export type MasterCsvErrorRow = { source: string; lineNumber: string; columnName: string; message: string };

// 表示する不備と、多すぎて隠した件数。
export type MasterCsvErrorView = { rows: MasterCsvErrorRow[]; hiddenCount: number };

function textOfLineNumber(lineNumber: number | null): string {
  if (lineNumber === null) {
    return "";
  }
  return String(lineNumber);
}

function limitedErrorViewOf(rows: readonly MasterCsvErrorRow[]): MasterCsvErrorView {
  const limited = limitedListOf(rows, MAX_DISPLAYED_ERROR_COUNT);
  return { rows: limited.items, hiddenCount: limited.hiddenCount };
}

// 不備一覧の1行分を組み立てる。前処理の不備もサーバーからの不備も、
// 出どころの作り方だけが違って残りの整え方は同じため、ここへまとめる。
function errorRowOf(source: string, lineNumber: number | null, columnName: string | null, message: string): MasterCsvErrorRow {
  return { source, lineNumber: textOfLineNumber(lineNumber), columnName: columnName ?? "", message };
}

// ブラウザ側の前処理で見つかった不備を一覧の形にする。出どころはファイル名で示す。
// hiddenErrorCount は、前処理の側がファイルごとの上限で先に切り捨てた件数。
// ここでの表示上の上限による切り捨て件数と合算し、利用者には1つの件数として伝える。
export function errorViewOfFileErrors(errors: readonly MasterCsvFileError[], hiddenErrorCount: number): MasterCsvErrorView {
  const rows = errors.map((error) => errorRowOf(error.fileName, error.lineNumber, error.columnName, error.message));
  const displayLimitedView = limitedErrorViewOf(rows);
  return { rows: displayLimitedView.rows, hiddenCount: displayLimitedView.hiddenCount + hiddenErrorCount };
}

// サーバーが返したテーブルの出どころを表す文字列。
// サーバーはテーブル名しか知らないため、そのテーブルを作った元のファイル名を取り込み対象から引き当てて併記する。
//
// ファイル名を添える理由は2つある。複数のファイルをまとめて投入したとき、どのファイルを表計算ソフトで
// 開いて直せばよいかが分かるようにするため。もう1つは、工種と種別と工程のように1つの画面が複数の
// テーブルを束ねている場合、画面名だけでは同じ名前が並んでどのテーブルの話か読み取れなくなるため。
//
// 取り込み対象の中に見当たらないテーブルでは、添えられるファイル名が無いため画面名だけを返す。
export function sourceLabelOfCollectionKey(collectionKey: string, tables: readonly MasterCsvTable[]): string {
  const screenLabel = screenLabelOfCollectionKey(collectionKey);
  const sourceTable = tables.find((table) => table.collectionKey === collectionKey);
  if (sourceTable !== undefined) {
    return `${screenLabel}(${sourceTable.sourceFileName})`;
  }
  return screenLabel;
}

// サーバーが返した不備を一覧の形にする。出どころはマスタ名と元のファイル名を併記して示す。
export function errorViewOfServerErrors(
  errors: readonly MasterImportError[],
  tables: readonly MasterCsvTable[],
): MasterCsvErrorView {
  const rows = errors.map((error) => {
    return errorRowOf(sourceLabelOfCollectionKey(error.collectionKey, tables), error.lineNumber, error.columnName, error.message);
  });
  return limitedErrorViewOf(rows);
}

// 隠した不備の件数を伝える文。件数だけでは次に何をすればよいか分からないため、進め方も添える。
// 隠していない場合は空文字を返し、画面には何も出さない。
export function hiddenErrorCountTextOf(hiddenCount: number): string {
  if (hiddenCount > 0) {
    return `他 ${hiddenCount} 件あります。表示した分を直して取り込み直すと、残りも確認できます。`;
  }
  return "";
}

// -----------------------------------------------------------------------------
// 衝突と再採番の一覧
// -----------------------------------------------------------------------------

// ID が既存行と重なったマスタと、その件数。
// collectionKey は一覧の行を1つずつ見分けるために持つ。表には出さない。
export type MasterCsvCollisionRow = { collectionKey: string; label: string; count: number };

// IDが重なったテーブルを一覧の形にする。
// 工種・種別・工程のように1つの画面に複数のテーブルが属する場合、画面名だけでは同じ行が並んで
// どのテーブルが何件重なったのか読み取れない。元のファイル名を併記して見分けられるようにする。
export function collisionRowsOf(
  collisions: readonly MasterImportCollision[],
  tables: readonly MasterCsvTable[],
): MasterCsvCollisionRow[] {
  return collisions.map((collision) => {
    return {
      collectionKey: collision.collectionKey,
      label: sourceLabelOfCollectionKey(collision.collectionKey, tables),
      count: collision.count,
    };
  });
}

// IDが重なった行の合計。IDをそのまま使う取り込みでは、この件数がそのまま上書きされる行数になる。
export function collidedRowCountOf(collisions: readonly MasterImportCollision[]): number {
  return collisions.reduce((total, collision) => total + collision.count, 0);
}

// この取り込みで既存の行が置き換わるかどうか。
// IDをそのまま使う取り込みで、なおかつIDが重なっているときだけ置き換えが起きる。
// 見出し・注意書き・件数の内訳がこの判断を別々に書くと、同じ画面の中で食い違うため1か所へまとめる。
function overwritesExistingRows(collisions: readonly MasterImportCollision[], idPolicy: MasterImportIdPolicy): boolean {
  return idPolicy === "keepId" && collisions.length > 0;
}

/**
 * 名前の変更予定を確かめてもらう画面の見出し文。
 *
 * IDをそのまま使う取り込みでIDが重なっているときは、名前を変えた行が新しく増えるのではなく、
 * 同じIDの既存の行を置き換える。この場合に「追加します」と書くと、取り返しのつかない上書きを
 * 軽い操作だと誤解させる。同じ画面の注意書きや件数の内訳とも食い違うため、見出しから書き分ける。
 */
export function renamePreviewHeadlineTextOf(
  collisions: readonly MasterImportCollision[],
  idPolicy: MasterImportIdPolicy,
): string {
  if (overwritesExistingRows(collisions, idPolicy)) {
    return "既存と同じ名前があるため、次の行は名前を変えて取り込みます。IDが重なった行は追加ではなく既存の行の上書きになります。意図しない変更が起きていないか確かめてください。";
  }
  return "既存と同じ名前があるため、次の行は名前を変えて追加します。意図しないマスタが増えていないか確かめてください。";
}

/**
 * IDが重なったマスタにだけ当てはまる注意書き。重なりが無いときは何も出さない。
 *
 * 同じ重なりでも、IDの決め方によって起きることが正反対になる。
 * 採番し直す取り込みでは全行が新しい行として増え、IDをそのまま使う取り込みでは既存の行が消えて置き換わる。
 * 取り返しのつかない側を「追加」と伝えてしまわないよう、方針ごとに文言を分ける。
 * IDをそのまま使う取り込みでも、重なっていない行は既存の続きの新しいIDで追加されるため、そこも併せて伝える。
 */
export function idCollisionNoticeTextOf(collisions: readonly MasterImportCollision[], idPolicy: MasterImportIdPolicy): string {
  const hasCollision = collisions.length > 0;
  if (overwritesExistingRows(collisions, idPolicy)) {
    return `IDが重なった${collidedRowCountOf(collisions)}行は、同じIDの既存の行を上書きします。上書きされる前の内容は元に戻せません。重なっていない行は、CSVのIDではなく既存の続きの新しいIDで追加されます。`;
  }
  if (hasCollision) {
    return "IDが重なったマスタは、重なっていない行も含めて全行が新しい行として追加されます。";
  }
  return "";
}

// 重複を避けるために書き換わる項目の呼び名。
// サーバーはデータベースの列名しか返さないため、そのまま出しても利用者には何の項目か伝わらない。
// マスタによって書き換わる項目が違い、工事マスタでは名前ではなく整理番号が変わる。
// ここに並べる列名は、サーバー側で自然キーを定義している naturalKeys.ts が書き換え対象にする列と対応する。
const RENAME_COLUMN_LABELS: readonly { columnName: string; label: string }[] = [
  { columnName: "name", label: "名前" },
  { columnName: "control_number", label: "整理番号" },
];

// 呼び名が決まっていない項目では、列名を出さずに「値」とだけ書く。
// 利用者に通じない列名をそのまま見せるより、何の値かを言い切らないほうが誤解を生まないため。
export function renameColumnLabelOf(columnName: string): string {
  const known = RENAME_COLUMN_LABELS.find((candidate) => candidate.columnName === columnName);
  if (known !== undefined) {
    return known.label;
  }
  return "値";
}

// 重複を避けるために値が書き換わる予定の行。
// collectionKey と columnName は一覧の行を1つずつ見分けるために持ち、表には columnLabel の側を出す。
export type MasterCsvRenameRow = {
  collectionKey: string;
  label: string;
  lineNumber: number;
  columnName: string;
  columnLabel: string;
  fromValue: string;
  toValue: string;
};

// 値が書き換わる行を一覧の形にする。
// 衝突の一覧と同じく、1つの画面に複数のテーブルが属する場合に備えて元のファイル名を併記する。
export function renameRowsOf(
  renames: readonly MasterImportRename[],
  tables: readonly MasterCsvTable[],
): MasterCsvRenameRow[] {
  return renames.map((rename) => {
    return {
      collectionKey: rename.collectionKey,
      label: sourceLabelOfCollectionKey(rename.collectionKey, tables),
      lineNumber: rename.lineNumber,
      columnName: rename.columnName,
      columnLabel: renameColumnLabelOf(rename.columnName),
      fromValue: rename.fromValue,
      toValue: rename.toValue,
    };
  });
}

// 一覧の行を見分ける文字列。同じテーブルの複数行が並ぶため、テーブル名だけでは1行を指し示せない。
export function renameRowKeyOf(row: MasterCsvRenameRow): string {
  return `${row.collectionKey}-${row.lineNumber}-${row.columnName}`;
}

// -----------------------------------------------------------------------------
// 取り込み規模の一覧
// -----------------------------------------------------------------------------

// 取り込み対象になったマスタ1つ分の表示内容。取り込む前に規模を把握できるようにするためのもの。
export type MasterCsvImportTargetRow = { collectionKey: string; text: string };

// マスタごとの取り込み結果の注記。IDが重なったマスタで何が起きるのかはIDの決め方で変わるため、
// 採番し直す取り込みでは全行が増えること、IDをそのまま使う取り込みでは重なった行だけが置き換わり
// 残りは既存の続きの新しいIDで増えることを書き分ける。
function collisionNoteOf(collidedRowCount: number, idPolicy: MasterImportIdPolicy): string {
  if (idPolicy === "keepId") {
    return ` — うち${collidedRowCount}行は同じIDの既存の行を上書きし、残りの行は既存の続きの新しいIDで追加されます`;
  }
  return " — 全行が新しい行として追加されます";
}

export function importTargetRowsOf(
  tables: readonly MasterCsvTable[],
  collisions: readonly MasterImportCollision[],
  idPolicy: MasterImportIdPolicy,
): MasterCsvImportTargetRow[] {
  return tables.map((table) => {
    const baseText = `${sourceLabelOfCollectionKey(table.collectionKey, tables)}: ${table.rows.length}行`;
    const collision = collisions.find((candidate) => candidate.collectionKey === table.collectionKey);
    if (collision !== undefined) {
      return { collectionKey: table.collectionKey, text: `${baseText}${collisionNoteOf(collision.count, idPolicy)}` };
    }
    return { collectionKey: table.collectionKey, text: baseText };
  });
}

/**
 * 確認画面に出す件数の内訳。
 * 名前が変わる行と変わらない行に加えて、新しく増える行と既存の行を置き換える行にも分ける。
 * 上書きは取り返しがつかないため、追加と同じ数え方にまとめない。
 */
export type MasterCsvRenameSummary = {
  totalRowCount: number;
  renamedRowCount: number;
  keptNameRowCount: number;
  addedRowCount: number;
  overwrittenRowCount: number;
};

/**
 * 上書きされる行数は、IDが重なった行数と同じになる。
 * 採番をやり直す取り込みでは重なったIDを付け直すため、上書きされる行は無い。
 */
function overwrittenRowCountOf(collisions: readonly MasterImportCollision[], idPolicy: MasterImportIdPolicy): number {
  if (overwritesExistingRows(collisions, idPolicy)) {
    return collidedRowCountOf(collisions);
  }
  return 0;
}

export function renameSummaryOf(
  tables: readonly MasterCsvTable[],
  renames: readonly MasterImportRename[],
  collisions: readonly MasterImportCollision[],
  idPolicy: MasterImportIdPolicy,
): MasterCsvRenameSummary {
  const totalRowCount = tables.reduce((total, table) => total + table.rows.length, 0);
  const renamedRowCount = renames.length;
  const overwrittenRowCount = overwrittenRowCountOf(collisions, idPolicy);
  return {
    totalRowCount,
    renamedRowCount,
    keptNameRowCount: totalRowCount - renamedRowCount,
    addedRowCount: totalRowCount - overwrittenRowCount,
    overwrittenRowCount,
  };
}

// 名前の変更予定の確認画面に出す件数の内訳。何行が増え、何行が置き換わるのかを読み取れるようにする。
export function renameSummaryTextOf(summary: MasterCsvRenameSummary, idPolicy: MasterImportIdPolicy): string {
  const totalText = `取り込む行は合計${summary.totalRowCount}行です。`;
  const hasOverwrite = idPolicy === "keepId" && summary.overwrittenRowCount > 0;
  if (hasOverwrite) {
    const rowKindText = `新しく追加する行が${summary.addedRowCount}行、同じIDの既存の行を上書き更新する行が${summary.overwrittenRowCount}行です。`;
    const nameText = `名前を変えて取り込む行が${summary.renamedRowCount}行、名前をそのまま取り込む行が${summary.keptNameRowCount}行あります。`;
    return `${totalText}${rowKindText}${nameText}`;
  }
  return `${totalText}名前を変えて追加する行が${summary.renamedRowCount}行、そのまま追加する行が${summary.keptNameRowCount}行あります。`;
}

// -----------------------------------------------------------------------------
// 送信内容と完了の知らせ
// -----------------------------------------------------------------------------

/**
 * 前処理の結果をサーバーへ送れる形にする。どのファイル由来かはブラウザ側だけの情報のため送らない。
 *
 * nameStamp には、試行の応答で返った取り込み時刻をそのまま渡す。渡さないとサーバーが本送信のときに
 * 時刻を取り直すため、確認画面に出した名前と実際に書き込まれる名前が秒単位でずれてしまう。
 * 試行の前でまだ受け取っていない段階では省略する。
 */
export function importRequestOf(
  tables: readonly MasterCsvTable[],
  mode: MasterImportMode,
  idPolicy: MasterImportIdPolicy,
  nameStamp?: string,
): MasterImportRequest {
  const requestTables = tables.map((table) => {
    return { collectionKey: table.collectionKey, rows: table.rows };
  });
  return { idPolicy, mode, nameStamp, tables: requestTables };
}

/**
 * 確認画面が利用者へ見せた取り込み内容を、承認の証として送れる形にする。
 * その画面を作った試行の応答の値をそのまま渡す。別の試行の値を混ぜると、
 * 利用者が見ていない内容を承認したことになってしまう。
 *
 * idPolicy も承認へ含める。同じ衝突と名前の変更でも、IDの決め方が違えば起きることは正反対になる。
 * 上書きを承認した内容が採番し直しの取り込みへ通らないよう、決め方まで含めて突き合わせられるようにする。
 */
export function approvalOf(
  collisions: readonly MasterImportCollision[],
  renames: readonly MasterImportRename[],
  idPolicy: MasterImportIdPolicy,
): MasterImportApproval {
  return { collisions: [...collisions], idPolicy, renames: [...renames] };
}

/**
 * 取り込みを確定する要求を作る。承認した内容を必ず添える。
 *
 * 確認画面を出してから取り込むまでの間に別の利用者がマスタを変えると、承認していない
 * 名前の変更や警告の無い上書きが混ざる。サーバーは書き込みの直前にこの内容と計画を突き合わせ、
 * 食い違えば何も書き込まずに知らせてくれる。
 */
export function applyRequestOf(
  tables: readonly MasterCsvTable[],
  idPolicy: MasterImportIdPolicy,
  nameStamp: string,
  approval: MasterImportApproval,
): MasterImportRequest {
  return { ...importRequestOf(tables, "apply", idPolicy, nameStamp), approval };
}

/**
 * 試行の応答を見て、次に利用者へ見せる段階を決める。
 *
 * 取り込めない不備があれば中止し、次にIDの重なり、次に名前の変更予定の順に確かめる。
 * 名前が変わる行はIDが重なっているかどうかに関わらず必ず見せる。
 * 名前の重複はサーバー側が取り込み時刻を付けて解消するため、そのまま進むと
 * 利用者が知らないうちに名前の違うマスタが増えてしまうため。
 */
export type MasterCsvCheckOutcome =
  | { name: "blocked"; errors: MasterImportError[] }
  | { name: "idCollision"; collisions: MasterImportCollision[]; renames: MasterImportRename[] }
  | { name: "renamePreview"; renames: MasterImportRename[] }
  | { name: "ready" };

export function checkOutcomeOf(response: MasterImportResponse): MasterCsvCheckOutcome {
  if (response.errors.length > 0) {
    return { name: "blocked", errors: [...response.errors] };
  }
  if (response.collisions.length > 0) {
    return { name: "idCollision", collisions: [...response.collisions], renames: [...response.renames] };
  }
  if (response.renames.length > 0) {
    return { name: "renamePreview", renames: [...response.renames] };
  }
  return { name: "ready" };
}

/**
 * 採番し直す取り込みの試行の応答を見て、次に利用者へ見せる段階を決める。
 *
 * 最初の試行を扱う checkOutcomeOf とは、IDの重なりの扱いが違う。
 * この段階では利用者が既に「新しく採番し直す」を選んでおり、重なったIDは付け直されるため、
 * 重なりを理由に扱いを選び直してもらう画面へ戻す必要がない。
 * 戻してしまうと、同じ選択を何度も求められて取り込みを終えられなくなる。
 * 一方で名前が変わる行は、採番し直しても利用者が知らないうちに名前の違うマスタが増えるため必ず見せる。
 */
export type MasterCsvRenumberCheckOutcome =
  | { name: "blocked"; errors: MasterImportError[] }
  | { name: "renamePreview"; collisions: MasterImportCollision[]; renames: MasterImportRename[] }
  | { name: "ready"; collisions: MasterImportCollision[] };

export function renumberCheckOutcomeOf(response: MasterImportResponse): MasterCsvRenumberCheckOutcome {
  const collisions = [...response.collisions];
  if (response.errors.length > 0) {
    return { name: "blocked", errors: [...response.errors] };
  }
  if (response.renames.length > 0) {
    return { name: "renamePreview", collisions, renames: [...response.renames] };
  }
  return { name: "ready", collisions };
}

/**
 * 本送信の応答の解釈。サーバーが取り込みを確定させたかどうかだけを表す。
 * approvalMismatch は、承認した内容と書き込む直前の内容が食い違って何も書き込まれなかった場合。
 * 組み直された新しい内容が返るため、それを利用者へ見せ直す。
 * nameStamp は組み直した計画が使った取り込み時刻で、送った時刻とは違うことがある。
 */
export type ApplyOutcome =
  | { kind: "applied"; message: string }
  | { kind: "approvalMismatch"; collisions: MasterImportCollision[]; nameStamp: string; renames: MasterImportRename[] }
  | { kind: "serverError"; errors: MasterImportError[] }
  | { kind: "failed"; message: string };

/**
 * 本送信の応答から、利用者へ何を伝えるかを決める。
 * 食い違いは応答の値で見分ける。文言を読み取ると、説明文を書き直した途端に判別が壊れるため。
 * 依頼そのものが例外で終わった場合の扱いは、通信を呼び出す画面側に残す。
 */
export function applyOutcomeOfResponse(response: MasterImportResponse): ApplyOutcome {
  if (response.approvalMismatch === true) {
    return { kind: "approvalMismatch", collisions: [...response.collisions], nameStamp: response.nameStamp, renames: [...response.renames] };
  }
  if (response.errors.length > 0) {
    return { kind: "serverError", errors: response.errors };
  }
  if (!response.ok) {
    return { kind: "failed", message: MASTER_IMPORT_APPLY_REJECTED_NOTICE };
  }
  return { kind: "applied", message: appliedSummaryTextOf(response.applied) };
}

// 取り込みが終わったことを伝える文。マスタごとに追加と更新の件数を並べる。
export function appliedSummaryTextOf(applied: readonly MasterImportApplied[]): string {
  const changedItems = applied.filter((item) => item.addedCount > 0 || item.updatedCount > 0);
  if (changedItems.length === 0) {
    return "取り込みましたが、変更された行はありませんでした。";
  }
  const parts = changedItems.map((item) => {
    return `${screenLabelOfCollectionKey(item.collectionKey)} 追加${item.addedCount}件 / 更新${item.updatedCount}件`;
  });
  return `マスタを取り込みました。${parts.join("、")}`;
}

// -----------------------------------------------------------------------------
// 画面に出す固定の文言
// -----------------------------------------------------------------------------
//
// 利用者へ見せる文言はすべてここに置き、ポップアップ側には並べる仕事だけを残す。
// 描画の中へ直に書くと、同じ画面の他の説明と食い違っても気づけず、検証の対象からも外れる。

// どのマスタのデータかは1行目だけで決まる。Excel で開いて編集する利用者が、
// 見出しの行だと思って1行目を消してしまうと取り込めなくなるため、消さないよう先に伝える。
export const MASTER_IMPORT_TABLE_ROW_NOTICE =
  "CSVの1行目には、どのマスタのデータかを示す「テーブル,マスタ名」の行が入っています。Excel などで編集するときは、この1行目を消したり書き換えたりしないでください。1行目が無いと取り込めません。";

// 取り込みで新しく増える行のIDが、CSVに書かれた番号のままにはならないことを先に伝える案内。
// CSVのIDをそのまま残せると思ったまま取り込むと、他の資料と番号が合わずに戸惑うため、
// CSVのIDが何に使われるのかまで書いておく。
export const MASTER_IMPORT_ID_RENUMBER_NOTICE =
  "新しく追加される行のIDは、取り込み時に既存の続きの連続した番号へ振り直されます。CSVのIDは、既存のどの行を上書きするかの照合にだけ使われます。";

// ファイルを読み込む前に見つかった不備を伝える見出し。サーバーへは何も送っていない。
export const MASTER_IMPORT_FILE_ERROR_NOTICE = "ファイルの内容に不備があります。直してからもう一度取り込んでください。";

// サーバーが取り込みを断ったことを伝える見出し。何も書き込まれていない事実を先に置く。
export const MASTER_IMPORT_SERVER_ERROR_NOTICE = "取り込めない内容がありました。何も書き込まずに中止しています。";

// 確認すべきことが何も無く、そのまま取り込める状態を伝える見出し。
export const MASTER_IMPORT_READY_NOTICE = "内容を確認しました。このまま取り込めます。";

// IDの重なりが見つかり、扱いを選んでもらう段階の見出し。
export const MASTER_IMPORT_COLLISION_NOTICE = "取り込むIDが既存の行と重なっています。どちらの扱いにするか選んでください。";

// IDをそのまま使う選択肢に添える注意書き。置き換わるのは行だけでなく、その行を指していた記録の意味も変わる。
export const MASTER_IMPORT_OVERWRITE_NOTICE =
  "IDをそのまま使うと、同じIDの既存の行が置き換わります。その行を参照している施工記録などは書き換え後の内容を指すことになり、過去の記録の意味が変わります。";

/**
 * 承認した内容と、書き込む直前に組み直された内容が食い違ったときの案内文。
 * 利用者の入力の誤りではないため、直す場所を探させない。
 * 何も書き込まれていない事実と、確かめ直せば取り込めることを伝える。
 */
export const MASTER_IMPORT_APPROVAL_CHANGED_NOTICE =
  "他の利用者がマスタを変更したため、取り込む内容が変わりました。何も書き込まずに中止しています。下の内容を確かめてから取り込んでください。";

// サーバーが取り込みを確定できなかったのに、直すべき不備も返ってこなかったときの案内文。
export const MASTER_IMPORT_APPLY_REJECTED_NOTICE = "取り込みに失敗しました。時間をおいてもう一度お試しください。";

// 待っている間に何をしているのかを伝える文。
export const MASTER_IMPORT_READING_FILES_MESSAGE = "ファイルを読み込んでいます…";
export const MASTER_IMPORT_CHECKING_MESSAGE = "サーバーで内容を確認しています…";
export const MASTER_IMPORT_RENUMBER_CHECKING_MESSAGE = "新しい番号での取り込み内容を確認しています…";
export const MASTER_IMPORT_APPLYING_MESSAGE = "取り込んでいます…";

// 例外で終わったときに、どの段階でつまずいたのかを示す見出し。importFailureTextOf へ渡す。
export const MASTER_IMPORT_CHECK_STAGE_LABEL = "サーバーへの確認に失敗しました";
export const MASTER_IMPORT_RENUMBER_CHECK_STAGE_LABEL = "新しい番号での確認に失敗しました";
export const MASTER_IMPORT_APPLY_STAGE_LABEL = "取り込みに失敗しました";

// -----------------------------------------------------------------------------
// うまくいかなかったときの伝え方
// -----------------------------------------------------------------------------

/**
 * 他の利用者と同時に同じマスタを取り込んでぶつかったときの案内文。
 * 入力の誤りではなく順番の問題なので、直す場所を探さずもう一度試せばよいことを伝える。
 */
export const MASTER_IMPORT_CONFLICT_NOTICE =
  "他の利用者が同時に同じマスタを変更したため取り込めませんでした。何も書き込まずに中止しています。もう一度お試しください。";

// 同時取り込みの競合かどうか。文言ではなくサーバーが返したエラーコードで見分ける。
export function isMasterImportConflict(error: unknown): boolean {
  return error instanceof ApiError && error.code === ErrorCode.MasterImportConflict;
}

/**
 * 取り込みの依頼が例外で終わったときの案内文。
 * 同時取り込みの競合だけは、やり直せば解決しうると分かる専用の文言にする。
 * それ以外は、どの段階でつまずいたのかが分かるよう見出しに理由を添える。
 */
export function importFailureTextOf(error: unknown, stageLabel: string): string {
  if (isMasterImportConflict(error)) {
    return MASTER_IMPORT_CONFLICT_NOTICE;
  }
  return `${stageLabel}：${errorMessageOf(error)}`;
}

// サーバーの取り込みは終わったのに、そのあとの画面の作り直しでつまずいたときの案内文。
// ここで「失敗しました」と伝えると、利用者が同じファイルを送り直して同じデータを二重に取り込んでしまう。
// 取り込みが済んでいる事実を先に置き、必要な操作は画面の再読み込みだけだと伝える。
export function importedButRefreshFailedTextOf(reason: string): string {
  return `取り込みは完了しましたが、画面の更新に失敗しました。画面を再読み込みしてください。理由：${reason}`;
}

// うまくいかなかった段階の見出しの色。取り込みが確定済みの場合は、赤ではなく注意の色で見せる。
export function failureSeverityOf(isImportFinished: boolean): "error" | "warning" {
  if (isImportFinished) {
    return "warning";
  }
  return "error";
}

// -----------------------------------------------------------------------------
// 取り込みの表示部品に出す固定の文言
// -----------------------------------------------------------------------------

// ファイルを受け取る領域の案内と、そこに置く操作の呼び名。
export const MASTER_IMPORT_DROP_AREA_MESSAGE = "CSVファイルまたは zip ファイルをここに置いてください。複数まとめて置けます。";
export const MASTER_IMPORT_FILE_INPUT_ARIA_LABEL = "取り込むファイル";

// 行番号がCSVのどの行を指すのかは見ただけでは決まらないため、数え方を見出しに添える。
export const MASTER_IMPORT_LINE_NUMBER_HEADER_LABEL = "CSVの行番号";
export const MASTER_IMPORT_LINE_NUMBER_HEADER_NOTE = "CSVの1行目(識別行)から数えた行番号です";

// 不備一覧の見出し。
export const MASTER_IMPORT_ERROR_SOURCE_HEADER_LABEL = "ファイル / マスタ";
export const MASTER_IMPORT_ERROR_COLUMN_HEADER_LABEL = "列名";
export const MASTER_IMPORT_ERROR_MESSAGE_HEADER_LABEL = "理由";

// IDが重なったマスタの一覧の見出し。
export const MASTER_IMPORT_SOURCE_HEADER_LABEL = "マスタ / ファイル";
export const MASTER_IMPORT_COLLISION_COUNT_HEADER_LABEL = "重なった件数";

// 値が書き換わる行の一覧の見出し。
export const MASTER_IMPORT_RENAME_LINE_NUMBER_HEADER_LABEL = "行番号";
export const MASTER_IMPORT_RENAME_COLUMN_HEADER_LABEL = "変わる項目";
export const MASTER_IMPORT_RENAME_FROM_HEADER_LABEL = "変更前";
export const MASTER_IMPORT_RENAME_TO_HEADER_LABEL = "変更後";

// -----------------------------------------------------------------------------
// 書き出しの画面に出す固定の文言
// -----------------------------------------------------------------------------

// 選んだマスタがすべて0件で、出力するファイルが1つも無かったときに選択ポップアップへ出す案内文。
export const MASTER_EXPORT_EMPTY_SELECTION_MESSAGE = "選んだマスタには書き出せる行がありません。行のあるマスタを選んでください。";

// 書き出しは常にサーバーの最新のマスタから作るため、通信できない間は始めさせない。
export const MASTER_EXPORT_OFFLINE_MESSAGE = "現在オフラインです。書き出しはサーバーの最新のマスタを取り直してから行うため、オンラインに復帰してから実行してください。";

// 待っている間に何をしているのかを伝える文。
export const MASTER_EXPORT_BUSY_MESSAGE = "最新のマスタを取り直してファイルを作っています…";

// 書き出したCSVは他の環境へ配る成果物になるため、端末に残っていた古い控えから作らせない。
export const MASTER_EXPORT_REFRESH_FAILED_PREFIX = "最新のマスタを取得できなかったため、書き出しを中止しました：";

// ファイルの組み立てそのものでつまずいたときの見出し。
export const MASTER_EXPORT_BUILD_FAILED_PREFIX = "CSVファイルの作成に失敗しました：";

// 選択ポップアップの説明。何を選べるのか、0件のマスタはどう扱われるのか、
// 押したあとに何が起きるのかを順に伝える。
export const MASTER_EXPORT_SELECTION_DESCRIPTION =
  "出力したいマスタを選んでください。1つだけならCSVファイル、2つ以上ならまとめた zip ファイルとして保存します。";
export const MASTER_EXPORT_EMPTY_MASTER_DESCRIPTION = "行が0件のマスタは書き出しません。選んだマスタがすべて0件の場合は何も出力しません。";
export const MASTER_EXPORT_REFRESH_DESCRIPTION =
  "ダウンロードを押すとサーバーから最新のマスタを取り直し、その内容で書き出します。取り直せなかった場合は書き出しません。";
